import { useState, useEffect, useCallback } from "react";
import { DateTime } from "luxon";
import { useScanData } from "../api";

const DownloadData = ({ samples, needsScanData }) => {
  //state variables
  const [ids, setIds] = useState([]);
  const [data, setData] = useState([]);
  const [isDataUpdated, setIsDataUpdated] = useState(false);
  const [shouldSaveData, setShouldSaveData] = useState(false);
  const [isButtonClicked, setIsButtonClicked] = useState(false);

  //fetching scan data from database
  const scanQueries = useScanData(needsScanData ? ids : []);

  useEffect(() => {
    //check if samples is an array and set data accordingly
    if (!Array.isArray(samples)) {
      setData([samples]);
    } else {
      setData(samples);
    }

    //set ids array if needsScanData is true and samples is an array
    if (needsScanData && Array.isArray(samples)) {
      const sampleIds = samples.map((d) => d.sampleId);
      setIds(sampleIds);
    }
  }, [samples, needsScanData]);

  //updating data with scan data from database
  useEffect(() => {
    if (
      needsScanData &&
      !isDataUpdated &&
      scanQueries.length > 0 &&
      scanQueries.every((q) => q.isSuccess) &&
      isButtonClicked
    ) {
      setData((prevData) => {
        const newData = prevData.map((d) => {
          const sample = scanQueries.find(
            (q) => q.data?.sampleId === d.sampleId
          );
          return { ...d, tests: sample?.data?.tests };
        });
        setIsDataUpdated(true);
        setShouldSaveData(true);
        return newData;
      });
    }
  }, [scanQueries, needsScanData, isDataUpdated]);

  //calls saveData function when data is updated
  useEffect(() => {
    if (isDataUpdated && shouldSaveData) {
      saveData();
      setShouldSaveData(false);
      setIsButtonClicked(false);
    }
  }, [isDataUpdated, shouldSaveData]);

  const formatData = useCallback(() => {
    const headers = [
      "SampleID",
      "Date",
      "Time",
      "Scan Mode",
      "Graphitization Quality",
    ];
    const sampleInfo = data?.map((d) =>
      d.tests
        ?.map((test) => {
          let localDate = DateTime.fromISO(d.processedDate).setZone("local");
          return [
            d.sampleId,
            localDate.toISODate(),
            localDate.toFormat("t"),
            test.scanMode,
            test.graphite_quality,
          ].join(",");
        })
        .join("\n")
    );

    const infoData = headers.join(",") + "\n" + sampleInfo.join("\n");

    const scanData =
      "," +
      (data[0]?.tests[0]?.wavenumber_plot || []).join(",") +
      "\n" +
      data
        .map((d) =>
          d.tests
            ?.map((test) => {
              return (
                d.sampleId + "," + (test.processed_data_plot || []).join(",")
              );
            })
            .join("\n")
        )
        .join("\n");
    return { scanData, infoData };
  }, [data]);

  const saveAs = useCallback((blob, fileName) => {
    if (!(blob instanceof Blob)) {
      blob = new Blob([blob], { type: "csv" });
    }
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
  }, []);

  const fetchData = useCallback(async () => {
    if (needsScanData) {
      try {
        await Promise.all(
          scanQueries.map((query) => {
            if (query.refetch) {
              return query.refetch();
            }
            return Promise.resolve();
          })
        );
        setIsDataUpdated(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  }, [needsScanData, scanQueries]);

  const saveData = useCallback(() => {
    const { scanData, infoData } = formatData();
    saveAs(scanData, "data.csv");
    saveAs(infoData, "info.csv");
  }, [formatData, saveAs]);

  return (
    <div className='d-flex justify-content-center'>
      <button
        className='btn btn-outline-primary'
        onClick={() => {
          setIsButtonClicked(true);
          needsScanData ? fetchData() : saveData();
        }}
      >
        Download Data
      </button>
    </div>
  );
};

export default DownloadData;
